import type {
  InventoryLocation,
  InventoryLocationType,
  StorageLocationNode
} from '../Types';

export interface StorageLocationOption {
  id: string;
  label: string;
}

export interface StorageLocationSelection {
  areaId: string;
  /** Set when the saved spot is a direct child of areaId */
  spotId: string;
}

export const getLocationTypeLabel = (type: InventoryLocationType): string => {
  const labels: Record<InventoryLocationType, string> = {
    room: 'Room',
    section: 'Section',
    shelf: 'Shelf',
    bin: 'Bin'
  };
  return labels[type];
};

const CHILD_LOCATION_TYPE: Record<
  InventoryLocationType,
  InventoryLocationType | null
> = {
  room: 'section',
  section: 'shelf',
  shelf: 'bin',
  bin: null
};

/** Next location type that can be created under the given parent (null at root → room). */
export const getCreatableChildLocationType = (
  parent: StorageLocationNode | null
): InventoryLocationType | null => {
  if (!parent) return 'room';
  return CHILD_LOCATION_TYPE[parent.location_type] ?? null;
};

export const appendLocationToTree = (
  nodes: StorageLocationNode[],
  node: StorageLocationNode,
  parentId?: string | null
): StorageLocationNode[] => {
  if (!parentId) {
    return [...nodes, node];
  }

  const clone = structuredClone(nodes);
  const parent = findStorageLocationNode(clone, parentId);
  if (!parent) {
    return [...nodes, node];
  }

  parent.children = [...(parent.children ?? []), node];
  return clone;
};

/** Build a nested tree from flat mock locations (preview mode). */
export const flatLocationsToTree = (
  locations: InventoryLocation[]
): StorageLocationNode[] => {
  const nodes = new Map<string, StorageLocationNode>();
  locations.forEach(loc => {
    nodes.set(loc.id, {
      id: loc.id,
      name: loc.name,
      code: loc.code,
      location_type: loc.locationType,
      sort_order: 0,
      active: loc.active,
      children: []
    });
  });

  const roots: StorageLocationNode[] = [];
  locations.forEach(loc => {
    const node = nodes.get(loc.id)!;
    if (loc.parentLocationId && nodes.has(loc.parentLocationId)) {
      nodes.get(loc.parentLocationId)!.children!.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
};

const walkTree = (
  nodes: StorageLocationNode[],
  visit: (
    node: StorageLocationNode,
    parent: StorageLocationNode | null
  ) => void,
  parent: StorageLocationNode | null = null
) => {
  nodes.forEach(node => {
    visit(node, parent);
    if (node.children?.length) {
      walkTree(node.children, visit, node);
    }
  });
};

/**
 * Backend-created holding spots (ensureDefaultStorageLocations).
 * Internal only — stock lands here until the user assigns a real area.
 * Matched by name/code; inventory locations have no isSystem flag.
 */
const SYSTEM_DEFAULT_LOCATION_NAMES = new Set([
  'default room',
  'default section'
]);
const SYSTEM_DEFAULT_LOCATION_CODES = new Set([
  'default',
  'default-room',
  'default-sec'
]);

export const isSystemDefaultStorageLocation = (
  node: Pick<StorageLocationNode, 'name' | 'code'>
): boolean => {
  const name = node.name?.trim().toLowerCase() ?? '';
  if (SYSTEM_DEFAULT_LOCATION_NAMES.has(name)) return true;
  const code = node.code?.trim().toLowerCase() ?? '';
  return SYSTEM_DEFAULT_LOCATION_CODES.has(code);
};

/** Strip system default room/section so they never appear in pickers or trees. */
export const filterUserVisibleStorageLocations = (
  nodes: StorageLocationNode[]
): StorageLocationNode[] =>
  nodes
    .filter(node => !isSystemDefaultStorageLocation(node))
    .map(node => ({
      ...node,
      children: filterUserVisibleStorageLocations(node.children ?? [])
    }));

export const flattenActiveStorageLocations = (
  nodes: StorageLocationNode[]
): StorageLocationNode[] => {
  const flat: StorageLocationNode[] = [];
  walkTree(nodes, node => {
    if (node.active && !isSystemDefaultStorageLocation(node)) flat.push(node);
  });
  return flat;
};

export const getRootStorageLocations = (
  nodes: StorageLocationNode[]
): StorageLocationNode[] =>
  nodes.filter(
    node => node.active && !isSystemDefaultStorageLocation(node)
  );

export const findStorageLocationNode = (
  nodes: StorageLocationNode[],
  id: string
): StorageLocationNode | null => {
  let found: StorageLocationNode | null = null;
  walkTree(nodes, node => {
    if (node.id === id) found = node;
  });
  return found;
};

const findRootForNode = (
  nodes: StorageLocationNode[],
  targetId: string
): StorageLocationNode | null => {
  for (const root of nodes) {
    if (root.id === targetId) return root;
    if (findStorageLocationNode(root.children ?? [], targetId)) {
      return root;
    }
  }
  return null;
};

const buildDescendantLabel = (path: string[]): string => {
  if (path.length <= 1) return path[0] ?? '';
  return path.slice(1).join(' › ');
};

/** Descendant storage spots under a selected area (shown only after the area is chosen). */
export const getDescendantOptionsForArea = (
  nodes: StorageLocationNode[],
  areaId: string
): StorageLocationOption[] => {
  const root = findStorageLocationNode(nodes, areaId);
  if (!root) return [];

  const options: StorageLocationOption[] = [];

  const collect = (node: StorageLocationNode, path: string[]) => {
    if (!node.active || isSystemDefaultStorageLocation(node)) return;
    if (node.id !== root.id) {
      options.push({
        id: node.id,
        label: buildDescendantLabel(path) || node.name
      });
    }
    node.children?.forEach(child => collect(child, [...path, child.name]));
  };

  collect(root, [root.name]);
  return options;
};

export const areaHasDescendants = (
  nodes: StorageLocationNode[],
  areaId: string
): boolean => getDescendantOptionsForArea(nodes, areaId).length > 0;

export const getActiveChildLocations = (
  node: StorageLocationNode
): StorageLocationNode[] =>
  (node.children ?? []).filter(
    child => child.active && !isSystemDefaultStorageLocation(child)
  );

export const locationHasActiveChildren = (node: StorageLocationNode): boolean =>
  getActiveChildLocations(node).length > 0;

export const findLocationNodePath = (
  nodes: StorageLocationNode[],
  locationId: string
): StorageLocationNode[] => {
  const path: StorageLocationNode[] = [];

  const walk = (list: StorageLocationNode[]): boolean => {
    for (const node of list) {
      path.push(node);
      if (node.id === locationId) return true;
      if (node.children?.length && walk(node.children)) return true;
      path.pop();
    }
    return false;
  };

  walk(nodes);
  return path;
};

export const formatLocationLabel = (node: StorageLocationNode): string => {
  const code = node.code?.trim();
  return code ? `${code} — ${node.name}` : node.name;
};

export const formatNumberedLocationLabel = (
  node: StorageLocationNode,
  index: number
): string => `${index + 1}. ${formatLocationLabel(node)}`;

/** Breadcrumb for the closed picker button (no list numbers). */
export const formatStorageLocationPath = (
  nodes: StorageLocationNode[],
  locationId: string | null | undefined
): string | null => {
  if (!locationId?.trim()) return null;

  const path = findLocationNodePath(nodes, locationId);
  if (path.length === 0) {
    const node = findStorageLocationNode(nodes, locationId);
    return node ? formatLocationLabel(node) : null;
  }

  return path.map(node => formatLocationLabel(node)).join(' › ');
};

export const resolveStorageLocationSelection = (
  nodes: StorageLocationNode[],
  locationId: string | null | undefined
): StorageLocationSelection => {
  if (!locationId?.trim()) {
    return { areaId: '', spotId: '' };
  }

  const root = findRootForNode(nodes, locationId);
  if (!root) {
    return { areaId: '', spotId: '' };
  }

  if (locationId === root.id) {
    return { areaId: root.id, spotId: '' };
  }

  return { areaId: root.id, spotId: locationId };
};

/** Fallback leaf when callers omit row locationId (bulk save default). */
export const pickDefaultSpotForArea = (
  nodes: StorageLocationNode[],
  areaId: string,
  preferredSpotId?: string | null
): string => {
  const children = getDescendantOptionsForArea(nodes, areaId);
  if (children.length === 0) return areaId;
  if (preferredSpotId && children.some(spot => spot.id === preferredSpotId)) {
    return preferredSpotId;
  }
  return children[0].id;
};
